import {gql} from '@apollo/client';

import {doQuerySafe} from 'graphql-client/ApolloWrappers';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {TeamPageOrganization} from 'orgs/TeamPage';
import {
  GetTeamPageOrganizationQuery,
  GetTeamPageOrganizationQueryVariables,
} from 'parties/organizations/__generated/GetTeamPageOrganizationQuery';
import * as net from 'util/Net';

type GetTeamPageOrganizationQuery_organization_InaccessibleInfoNode = ExtractByTypename<
  GetTeamPageOrganizationQuery['organization'],
  'InaccessibleInfoNode'
>;
export enum OrganizationMembershipOperation {
  ADD_MEMBER = 'ADD_MEMBER',
  REMOVE_MEMBER = 'REMOVE_MEMBER',
}

export type InaccessibleOrganizationGraphQL = GetTeamPageOrganizationQuery_organization_InaccessibleInfoNode;
export type OrganizationGraphQL = GetTeamPageOrganizationQuery['organization'];

export function updateOrganizationMembers(
  userIds: Array<string>,
  organizationId: string,
  operation: OrganizationMembershipOperation
): Promise<{}> {
  return net.patchJSONPromise({
    url: `/1/api/organizations/${organizationId}/members`,
    data: {
      updates: userIds.map((userId) => ({userId, operation})),
    },
  });
}

export const ORGANIZATION_FRAGMENT = gql`
  fragment OrganizationPageOrganization on OrganizationNode {
    id
    handle
    name
    urlIdentifier
    admin
    member
    avatarUrl
    disablePersonalOwnables
    disablePublic
    email
  }
`;

export const GET_ORGANIZATION_QUERY = gql`
  query GetTeamPageOrganizationQuery($orgId: StrictString!) @skipAudit {
    organization(id: $orgId) {
      ... on OrganizationNode {
        ...OrganizationPageOrganization
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${ORGANIZATION_FRAGMENT}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export async function fetchTeamPageOrganization(orgId: string): Promise<TeamPageOrganization> {
  const result = await doQuerySafe<GetTeamPageOrganizationQuery, GetTeamPageOrganizationQueryVariables>(
    {query: GET_ORGANIZATION_QUERY},
    {
      variables: {
        orgId,
      },
    }
  );
  const organization = result.organization;
  if (isOrganizationInaccessible(organization)) {
    throw new Error(`Organization ${orgId} not found.`);
  }
  return {
    ...organization,
    url_identifier: organization.urlIdentifier,
    avatar_url: organization.avatarUrl ?? '',
  } as TeamPageOrganization;
}

export function isOrganizationInaccessible(
  organization: OrganizationGraphQL
): organization is InaccessibleOrganizationGraphQL {
  return (organization as InaccessibleOrganizationGraphQL).invalidId !== undefined;
}
